import { Button, Dialog, Typography } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import { Skeleton } from "@material-ui/lab";
import firebase from "firebase/app";
import React, { MouseEventHandler, Reducer, RefCallback, useCallback, useEffect, useMemo, useReducer } from "react";
import { auth, ui } from "../firebase";

type Props = {

}

type User = firebase.User | null

type State = {
    loginUiOpen: boolean,
    initialized: boolean,
    user: User,
}

type Action = { type: 'openDialog' } | { type: 'closeDialog' } | {
    type: 'initialize',
    user: User
} | {
    type: 'login',
    user: User
} | { type: 'setLoading' }

const reducer: Reducer<State, Action> = (state, action) => {
    const patch = (update: Partial<State>) => ({
        ...state,
        ...update
    });
    switch (action.type) {
        case 'openDialog': return patch({
            loginUiOpen: true
        });
        case 'closeDialog': return patch({
            loginUiOpen: false
        });
        case 'setLoading': return patch({ initialized: false });
        case 'login': return patch({ user: action.user, loginUiOpen: false });
        case 'initialize': return patch({ user: action.user, initialized: true });
        default: return state;
    }
}

const initial: State = {
    loginUiOpen: false,
    user: null,
    initialized: false,
}

export function useAuth() {
    const [state, dispatch] = useReducer<typeof reducer>(reducer, initial)

    useEffect(() => {
        firebase.auth().onAuthStateChanged(user => {
            dispatch({ type: 'initialize', user: user })
        })
    }, [])

    return [state, dispatch] as const
}

const Auth: React.FC<Props> = () => {
    const [state, dispatch] = useAuth();

    const loginUi: RefCallback<HTMLDivElement> = useCallback(node => {
        if (node) {
            ui.start(node, {
                //signInFlow: 'popup',
                callbacks: {
                    signInSuccessWithAuthResult: function (authResult, redirectUrl) {
                        dispatch({ type: 'login', user: authResult.user })
                        return false;
                    }
                },
                signInSuccessUrl: `/`,
                signInOptions: [
                    firebase.auth.EmailAuthProvider.PROVIDER_ID,
                    firebase.auth.GoogleAuthProvider.PROVIDER_ID
                ],
                tosUrl: `/`,
                privacyPolicyUrl: `/`
            });
        }
    }, [dispatch])

    const isLoggedIn = useMemo(() => {
        return !!state.user
    }, [state])

    const buttonClicked: MouseEventHandler<HTMLButtonElement> = useCallback((e) => {
        if (!isLoggedIn) {
            dispatch({ type: 'openDialog' })
        }
        else {
            dispatch({ type: 'setLoading' })
            setTimeout(() => {
                auth.signOut();
            }, 500)
        }
    }, [isLoggedIn, dispatch])

    const dispatchCallback = useCallback((action) => {
        return () => dispatch({ type: action })
    }, [dispatch])

    if (ui.isPendingRedirect() && !state.loginUiOpen) {
        dispatch({ type: 'openDialog' })
    }

    if (!state.initialized) {
        return <>
            <Skeleton height={45} width={76} />
        </>
    }

    return <Box display="flex" alignItems="center">
        {isLoggedIn && <Typography>Logged in as {state.user?.displayName}</Typography>}&nbsp;&nbsp;
        <Button onClick={buttonClicked} variant="outlined" color="primary">{isLoggedIn ? 'Logout' : 'Login'}</Button>
        <Dialog keepMounted={true} disablePortal={true} onClose={dispatchCallback('closeDialog')} open={state.loginUiOpen}>
            <div ref={loginUi} />
        </Dialog>
    </Box>
}

export default Auth;